import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Badge, Spinner, Alert, Table } from "react-bootstrap";
import api from "../../api";
import Can from "../Can";
import { usePermissions } from "../../context/PermissionContext";

const TAT_UNITS = ["minutes", "hours", "days"];

function formatDate(dt) {
  if (!dt) return "-";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return "-";
  }
}

// Phase 2C. Reusable TAT panel for the task detail view (§20). Distinguishes
// Configured TAT / Current Due Date / Requested Change / Approved Change —
// only ever acts through the backend's permission-gated APIs; there is no
// direct due_at form field a user could edit client-side.
export default function TaskTatPanel({ taskId, onChanged }) {
  const { can } = usePermissions();

  const [tat, setTat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [showChangeForm, setShowChangeForm] = useState(false);
  const [changeValue, setChangeValue] = useState("");
  const [changeUnit, setChangeUnit] = useState("hours");
  const [changeReason, setChangeReason] = useState("");

  const [showPauseForm, setShowPauseForm] = useState(false);
  const [pauseReasonType, setPauseReasonType] = useState("MANUAL");
  const [pauseReason, setPauseReason] = useState("");

  const load = async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks/${taskId}/tat`);
      setTat(data?.data || null);
    } catch (err) {
      console.error("Load task TAT error:", err);
      setTat(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get(`/tasks/${taskId}/tat/history`);
      setHistory(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      console.error("Load TAT history error:", err);
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && !history) loadHistory();
  };

  const isOverdue = useMemo(() => {
    if (!tat?.due_at || tat.is_paused) return false;
    return new Date(tat.due_at).getTime() < Date.now();
  }, [tat]);

  const submitChangeRequest = async () => {
    if (!changeValue || Number(changeValue) <= 0) {
      alert("Please enter a positive TAT value.");
      return;
    }
    setActing(true);
    try {
      await api.post(`/tasks/${taskId}/tat/change-requests`, {
        requested_value: Number(changeValue),
        requested_unit: changeUnit,
        reason: changeReason.trim() || undefined,
      });
      setShowChangeForm(false);
      setChangeValue("");
      setChangeReason("");
      await load();
      onChanged?.();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to submit TAT change request.");
    } finally {
      setActing(false);
    }
  };

  const approveRequest = async (requestId) => {
    setActing(true);
    try {
      await api.post(`/tasks/${taskId}/tat/change-requests/${requestId}/approve`, {});
      await load();
      onChanged?.();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to approve TAT change.");
    } finally {
      setActing(false);
    }
  };

  const rejectRequest = async (requestId) => {
    const remarks = window.prompt("Reason for rejecting this TAT change (optional):") || "";
    setActing(true);
    try {
      await api.post(`/tasks/${taskId}/tat/change-requests/${requestId}/reject`, { remarks });
      await load();
      onChanged?.();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to reject TAT change.");
    } finally {
      setActing(false);
    }
  };

  const submitPause = async () => {
    setActing(true);
    try {
      await api.post(`/tasks/${taskId}/tat/pause`, {
        pause_reason_type: pauseReasonType,
        reason: pauseReason.trim() || undefined,
      });
      setShowPauseForm(false);
      setPauseReason("");
      await load();
      onChanged?.();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to pause TAT.");
    } finally {
      setActing(false);
    }
  };

  const resume = async () => {
    setActing(true);
    try {
      await api.post(`/tasks/${taskId}/tat/resume`, {});
      await load();
      onChanged?.();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to resume TAT.");
    } finally {
      setActing(false);
    }
  };

  const recalculate = async () => {
    setActing(true);
    try {
      const { data } = await api.post(`/tasks/${taskId}/tat/recalculate`, {});
      alert(data?.message || "Recalculated.");
      await load();
      onChanged?.();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to recalculate TAT.");
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-3">
        <Spinner size="sm" animation="border" /> Loading TAT...
      </div>
    );
  }

  if (!tat) {
    return <div className="text-muted p-3">TAT information is not available for this task.</div>;
  }

  return (
    <div className="task-tat-panel">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <Badge bg="light" text="dark">Configured TAT: {tat.tat_value_applied ?? "—"} {tat.tat_unit_applied || ""}</Badge>
        <Badge bg="light" text="dark">Source: {tat.tat_source || "—"}</Badge>
        {tat.is_paused && <Badge bg="warning" text="dark">Paused</Badge>}
        {isOverdue && <Badge bg="danger">Overdue</Badge>}
      </div>

      <div className="mb-2">
        <strong>Current Due Date:</strong> {formatDate(tat.due_at)}
      </div>

      {tat.total_paused_business_minutes > 0 && (
        <div className="mb-2 small text-muted">
          Total paused business time: {tat.total_paused_business_minutes} minutes
        </div>
      )}

      {tat.rule && (
        <div className="mb-2 small text-muted">
          Reopen policy: {tat.rule.reopen_policy} {tat.rule.requires_change_approval ? "(changes need approval)" : ""}
        </div>
      )}

      <div className="mb-2 small text-muted">
        Last changed: {formatDate(tat.tat_last_changed_at)}
      </div>

      {tat.active_pause && (
        <Alert variant="warning" className="py-2 px-3">
          Paused since {formatDate(tat.active_pause.paused_at)} — {tat.active_pause.pause_reason_type}
          {tat.active_pause.reason ? `: ${tat.active_pause.reason}` : ""}
        </Alert>
      )}

      {tat.pending_requests?.length > 0 && (
        <Alert variant="info" className="py-2 px-3">
          <div className="fw-semibold mb-1">Requested Change{tat.pending_requests.length > 1 ? "s" : ""} (awaiting approval)</div>
          {tat.pending_requests.map((r) => (
            <div key={r.id} className="d-flex flex-wrap align-items-center gap-2 mb-1">
              <span>{r.requested_value} {r.requested_unit} → {formatDate(r.requested_due_at)} (by {r.requested_by_name || "—"})</span>
              <Can moduleKey="tasks" actionKey="approve_tat_change">
                <Button size="sm" variant="outline-success" onClick={() => approveRequest(r.id)} disabled={acting}>Approve</Button>
              </Can>
              <Can moduleKey="tasks" actionKey="reject_tat_change">
                <Button size="sm" variant="outline-danger" onClick={() => rejectRequest(r.id)} disabled={acting}>Reject</Button>
              </Can>
            </div>
          ))}
        </Alert>
      )}

      <div className="d-flex flex-wrap gap-2 mt-2">
        {(can("tasks", "request_tat_change") || can("tasks", "set_tat")) && !showChangeForm && (
          <Button size="sm" variant="outline-primary" onClick={() => setShowChangeForm(true)}>
            {can("tasks", "set_tat") ? "Set / Request TAT Change" : "Request TAT Change"}
          </Button>
        )}

        {tat.is_paused ? (
          <Can moduleKey="tasks" actionKey="resume_tat">
            <Button size="sm" variant="outline-success" onClick={resume} disabled={acting}>Resume</Button>
          </Can>
        ) : (
          <Can moduleKey="tasks" actionKey="pause_tat">
            {!showPauseForm && (
              <Button size="sm" variant="outline-secondary" onClick={() => setShowPauseForm(true)}>Pause</Button>
            )}
          </Can>
        )}

        <Can moduleKey="tasks" actionKey="recalculate_tat">
          <Button size="sm" variant="outline-dark" onClick={recalculate} disabled={acting}>Recalculate</Button>
        </Can>

        <Can moduleKey="tasks" actionKey="view_tat_history">
          <Button size="sm" variant="outline-secondary" onClick={toggleHistory}>
            {showHistory ? "Hide History" : "View History"}
          </Button>
        </Can>
      </div>

      {showChangeForm && (
        <div className="border rounded p-3 mt-2 bg-white">
          <div className="row g-2">
            <div className="col-4">
              <Form.Control type="number" placeholder="Value" value={changeValue} onChange={(e) => setChangeValue(e.target.value)} />
            </div>
            <div className="col-4">
              <Form.Select value={changeUnit} onChange={(e) => setChangeUnit(e.target.value)}>
                {TAT_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </Form.Select>
            </div>
            <div className="col-12">
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Reason (optional)"
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
              />
            </div>
            <div className="col-12 d-flex gap-2">
              <Button size="sm" onClick={submitChangeRequest} disabled={acting}>Submit</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setShowChangeForm(false)} disabled={acting}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {showPauseForm && (
        <div className="border rounded p-3 mt-2 bg-white">
          <div className="row g-2">
            <div className="col-6">
              <Form.Select value={pauseReasonType} onChange={(e) => setPauseReasonType(e.target.value)}>
                <option value="MANUAL">Manual</option>
                <option value="WAITING_FOR_INFORMATION">Waiting for information</option>
                <option value="EXTERNAL_DEPENDENCY">External dependency</option>
                <option value="APPROVAL_WAIT">Approval wait</option>
                <option value="OTHER">Other</option>
              </Form.Select>
            </div>
            <div className="col-12">
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Reason (optional)"
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
              />
            </div>
            <div className="col-12 d-flex gap-2">
              <Button size="sm" onClick={submitPause} disabled={acting}>Pause</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setShowPauseForm(false)} disabled={acting}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="mt-3">
          {loadingHistory ? (
            <Spinner size="sm" animation="border" />
          ) : history?.length ? (
            <Table responsive size="sm" className="unit-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Old Due</th>
                  <th>New Due</th>
                  <th>Source</th>
                  <th>By</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td>{h.event_type}</td>
                    <td>{formatDate(h.old_due_at)}</td>
                    <td>{formatDate(h.new_due_at)}</td>
                    <td>{h.new_source || h.old_source || "—"}</td>
                    <td>{h.changed_by_name || "—"}</td>
                    <td>{formatDate(h.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-muted">No TAT history yet.</div>
          )}
        </div>
      )}
    </div>
  );
}
